import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { nn } from "date-fns/locale";
import { BellOff, CalendarPlus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatTile, StatGroup } from "@/components/admin/StatTile";
import { ActivityChart } from "@/components/admin/ActivityChart";
import { SectionHeading } from "@/components/admin/PageHeader";
import { ErrorState } from "@/components/admin/states/ErrorState";
import { WeekStrip } from "@/components/admin/dashboard/WeekStrip";
import { TerminlistePanel } from "@/components/admin/dashboard/TerminlistePanel";
import { NeedsAction } from "@/components/admin/dashboard/NeedsAction";
import { NextEventCard } from "@/components/admin/dashboard/NextEventCard";
import { LatestActivity } from "@/components/admin/dashboard/LatestActivity";
import { entryItem, isoWeek, pendingItem, recurringItems, roomColourMap, weekDays } from "@/components/admin/calendar/engine";
import { adminBookings, adminCalendar, adminDashboard, adminRecurring, adminRooms } from "@/lib/api";
import { toISODate, capitalize } from "@/lib/format";

const greeting = () => { const h = new Date().getHours(); return h < 5 ? "God natt" : h < 10 ? "God morgon" : h < 17 ? "God dag" : "God kveld"; };
const kr = (n) => `${Math.round(n || 0).toLocaleString("nb-NO")} kr`;
const Reveal = ({ i, children, className }) => <div className={`reveal-up ${className || ""}`} style={{ "--i": i }}>{children}</div>;

export default function Dashboard() {
  const dashQ = useQuery({ queryKey: ["admin-dashboard"], queryFn: adminDashboard, refetchInterval: 60000 });
  const calQ = useQuery({ queryKey: ["admin-calendar"], queryFn: () => adminCalendar({}), staleTime: 30_000 });
  const recQ = useQuery({ queryKey: ["admin-recurring"], queryFn: adminRecurring, staleTime: 60_000 });
  const roomsQ = useQuery({ queryKey: ["admin-rooms"], queryFn: adminRooms, staleTime: 60_000 });
  const bookQ = useQuery({ queryKey: ["admin-bookings"], queryFn: () => adminBookings(), staleTime: 30_000 });
  const d = dashQ.data;

  const today = new Date();
  const todayISO = toISODate(today);
  const days = weekDays(today);
  const rooms = useMemo(() => roomsQ.data || [], [roomsQ.data]);
  const colours = useMemo(() => roomColourMap(rooms), [rooms]);
  const roomName = (id) => rooms.find((r) => r.id === id)?.short_name || id;

  const entries = useMemo(() => calQ.data?.entries || [], [calQ.data]);
  const tbd = calQ.data?.tbd || [];
  const upcoming = useMemo(() => entries.filter((e) => (e.end_date || e.start_date) >= todayISO), [entries, todayISO]);
  const past = useMemo(() => entries.filter((e) => (e.end_date || e.start_date) < todayISO).reverse(), [entries, todayISO]);

  const weekItems = useMemo(() => {
    const from = toISODate(days[0]), to = toISODate(days[6]);
    return [...entries.map(entryItem), ...(bookQ.data || []).filter((b) => b.status === "ny" && b.start_date).map(pendingItem), ...recurringItems(recQ.data || [], from, to, Object.fromEntries(rooms.map((r) => [r.id, r.short_name || r.name])))];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, bookQ.data, recQ.data, rooms]);
  const weekCount = weekItems.filter((it) => it.kind === "entry" && it.start_date <= toISODate(days[6]) && it.end_date >= toISODate(days[0])).length;
  const pendingBookings = (bookQ.data || []).filter((b) => b.status === "ny");
  const needs = (d?.new_bookings || 0) + (d?.unread_messages || 0) + (d?.pending_tickets || 0) + (d?.pending_vipps_requests || 0);

  if (dashQ.isError) return <ErrorState title="Kunne ikkje hente oversikta" text={dashQ.error?.message} onRetry={dashQ.refetch} />;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <Reveal i={0} className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <section className="min-w-0" data-testid="dashboard-hero">
          <p className="t-mono-sm flex items-center gap-2 text-muted-foreground" data-testid="dashboard-date"><span className="live-dot" aria-hidden="true" />{capitalize(format(today, "EEEE d. MMMM yyyy", { locale: nn }))} · Veke {isoWeek(today)}</p>
          <h1 className="mt-2.5 font-display text-[30px] font-semibold leading-[0.95] tracking-[-0.03em] sm:text-[36px]" data-testid="dashboard-title">{greeting()}.</h1>
          {dashQ.isLoading ? <Skeleton className="mt-3 h-4 w-64" /> : (
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[14px] text-muted-foreground" data-testid="dashboard-summary">
              <span>{needs === 0 ? "Alt er handsama – huset er i rute." : <>{needs === 1 ? "1 ting ventar" : `${needs} ting ventar`} på styret.</>}{weekCount > 0 && <> {weekCount === 1 ? "1 oppføring" : `${weekCount} oppføringar`} i kalenderen denne veka.</>}</span>
              {!d?.email_notifications_enabled && <Link to="/admin/innstillingar" className="inline-flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-[12px] font-medium text-foreground shadow-[0_0_0_1px_hsl(var(--foreground)/0.08)] transition-shadow hover:shadow-[0_0_0_1px_hsl(var(--foreground)/0.4)]" data-testid="dashboard-email-off"><BellOff className="h-3.5 w-3.5" /> E-postvarsling er av</Link>}
            </div>
          )}
        </section>
        <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex">
          <Button asChild variant="outline" className="h-9 bg-card" data-testid="dashboard-new-event"><Link to="/admin/arrangement?ny=1"><Sparkles className="h-4 w-4" /> Nytt arrangement</Link></Button>
          <Button asChild className="h-9" data-testid="dashboard-new-entry"><Link to="/admin/kalender?ny=1"><CalendarPlus className="h-4 w-4" /> Ny oppføring</Link></Button>
        </div>
      </Reveal>

      {dashQ.isLoading ? <Skeleton className="h-[84px] rounded-2xl" /> : (
        <StatGroup testId="dashboard-stats" className="reveal-up" style={{ "--i": 1 }}>
          <StatTile label="Nye førespurnader" value={d.new_bookings} to="/admin/bookingar" attention={d.new_bookings > 0} series={d.series?.bookings} hint="siste 8 veker" testId="stat-new-bookings" />
          <StatTile label="Uleste meldingar" value={d.unread_messages} to="/admin/meldingar" attention={d.unread_messages > 0} series={d.series?.messages} hint="siste 8 veker" testId="stat-unread-messages" />
          <StatTile label="Billettar selde" value={d.tickets?.sold ?? 0} to="/admin/billettar" attention={d.pending_tickets > 0} series={d.series?.tickets} hint={d.pending_tickets > 0 ? `${d.pending_tickets} ventar på betaling` : `${kr(d.tickets?.revenue)} omsett`} testId="stat-tickets" />
          <StatTile label="Terminlista" value={calQ.isLoading ? "–" : entries.length + tbd.length} to="/admin/kalender?view=list&omfang=alle" hint={calQ.isLoading ? "Hentar…" : `${upcoming.length} komande · ${tbd.length} reserverte utan dato`} testId="stat-terminliste" />
        </StatGroup>
      )}

      <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px] xl:gap-10">
        <div className="min-w-0 space-y-8">
          <Reveal i={2}><WeekStrip days={days} items={weekItems} colours={colours} loading={calQ.isLoading} weekCount={weekCount} /></Reveal>
          <Reveal i={3}><TerminlistePanel upcoming={upcoming} past={past} tbd={tbd} roomName={roomName} colours={colours} loading={calQ.isLoading || roomsQ.isLoading} /></Reveal>
        </div>
        <aside className="min-w-0 space-y-8">
          <Reveal i={2}><NeedsAction d={d} pendingBookings={pendingBookings} loading={dashQ.isLoading} /></Reveal>
          <Reveal i={3}><NextEventCard event={d?.next_event} loading={dashQ.isLoading} /></Reveal>
          <Reveal i={4}>
            <section data-testid="dashboard-chart">
              <SectionHeading title="Aktivitet siste 8 veker" description="Førespurnader, meldingar og selde billettar per veke" />
              {dashQ.isLoading ? <Skeleton className="h-[160px] rounded-2xl" /> : <ActivityChart series={d.series} height={160} />}
            </section>
          </Reveal>
          <Reveal i={5}><LatestActivity activity={d?.activity} loading={dashQ.isLoading} /></Reveal>
        </aside>
      </div>
    </div>
  );
}
